import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { PawPrint, ArrowRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const cardVariants = {
  hidden: { opacity: 0, y: 56 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: EASE } },
};

export const DoorwayCard = ({ room, index, onOpen }) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(
    scrollYProgress,
    [0, 1],
    reduceMotion ? ["0%", "0%"] : ["-5%", "5%"]
  );

  const isRight = room.side === "right";
  const textProtect =
    room.id === "freebies" ? "text-protect-strong" : "text-protect";

  return (
    <motion.button
      ref={ref}
      type="button"
      data-testid={`doorway-card-${room.id}`}
      aria-label={room.href ? `${room.name} — open now` : `${room.name} — coming soon. Notify me.`}
      variants={cardVariants}
      whileHover={reduceMotion ? undefined : { y: -8 }}
      transition={{ duration: 0.5, ease: EASE }}
      onClick={() => (room.href ? navigate(room.href) : onOpen(room))}
      className="group relative block w-full cursor-pointer overflow-hidden rounded-[26px] text-left shadow-[0_18px_50px_-24px_rgba(42,37,30,0.35)] transition-shadow duration-500 hover:shadow-[0_28px_70px_-24px_rgba(42,37,30,0.45)] sm:rounded-[30px] aspect-[5/4] sm:aspect-[16/9] lg:aspect-auto lg:h-[270px] xl:h-[315px] 2xl:h-[360px]"
    >
      <motion.div style={{ y: parallaxY }} className="absolute -inset-y-[6%] inset-x-0">
        <img
          src={room.image}
          srcSet={`${room.imageSm} 480w, ${room.image} 800w`}
          sizes="(max-width: 1024px) 100vw, 50vw"
          alt={room.alt}
          loading={index > 1 ? "lazy" : "eager"}
          fetchpriority={index === 0 ? "high" : "auto"}
          draggable={false}
          className="h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.045]"
          style={{ objectPosition: room.position }}
        />
      </motion.div>

      <div
        className={`absolute inset-y-0 flex w-full flex-col items-start justify-center px-[7%] ${
          isRight ? "sm:items-end sm:text-right" : "sm:items-start sm:text-left"
        }`}
      >
        <div className={`relative max-w-[62%] sm:max-w-[46%] ${isRight ? "max-sm:ml-auto max-sm:text-right" : ""}`}>
          <span className="relative block">
            <PawPrint
              aria-hidden
              className="h-5 w-5 sm:h-[22px] sm:w-[22px]"
              style={{ color: room.accent, filter: "drop-shadow(0 0 4px rgba(255,250,240,1)) drop-shadow(0 0 10px rgba(255,250,240,0.95)) drop-shadow(0 0 20px rgba(255,250,240,0.8))" }}
              fill={room.accent}
              strokeWidth={0}
            />
            <span className={`${textProtect} mt-2 block font-serif text-[34px] font-medium leading-none tracking-tight text-ink sm:mt-3 sm:text-4xl lg:text-[40px] xl:text-[46px]`}>
              {room.name}
            </span>
            <span className={`${textProtect} mt-3 block text-[9.5px] font-semibold uppercase leading-[1.9] tracking-[0.2em] text-ink sm:mt-4 sm:text-[11px]`}>
              {room.lines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </span>
            <span
              aria-hidden
              className="mt-4 flex h-11 w-11 items-center justify-center rounded-full transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1.5 sm:mt-5 sm:h-12 sm:w-12"
              style={{ backgroundColor: room.accent }}
            >
              <ArrowRight className="h-[18px] w-[18px] text-ivory" />
            </span>
          </span>
        </div>
      </div>
    </motion.button>
  );
};
